import React, { useState, useEffect } from 'react';
import './NumberStepsGenerator.css';

const NumberStepsGenerator = () => {
  const [seed, setSeed] = useState(0);
  const [target, setTarget] = useState(23);
  const [numOps, setNumOps] = useState(4);
  const [maxSteps, setMaxSteps] = useState(12);
  const [status, setStatus] = useState('');
  const [question, setQuestion] = useState('');
  const [ops, setOps] = useState([]);
  const [unusualIdx, setUnusualIdx] = useState(-1);
  const [meta, setMeta] = useState('');
  const [pathStates, setPathStates] = useState('');
  const [pathStatesWithActions, setPathStatesWithActions] = useState('');

  // Deterministic RNG
  function mulberry32(a) {
    return function() {
      let t = a += 0x6D2B79F5;
      t = Math.imul(t ^ t >>> 15, t | 1);
      t ^= t + Math.imul(t ^ t >>> 7, t | 61);
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  function seededInt(rng, lo, hi) {
    return Math.floor(rng() * (hi - lo + 1)) + lo;
  }

  function choice(rng, arr) {
    return arr[Math.floor(rng() * arr.length)];
  }

  // Operation constructor
  function makeOps(seed, numOps) {
    const rng = mulberry32(seed >>> 0);
    const ops = [];
    let hasNegative = false;
    for (let i = 0; i < numOps; i++) {
      const t = choice(rng, ['add', 'add', 'add', 'mul', 'sub', 'sub']);
      if (t === 'mul') {
        const k = choice(rng, [2, 2, 2, 3]);
        ops.push({ kind: 'mul', val: k, label: `×${k}` });
      } else if (t === 'add') {
        const a = seededInt(rng, 1, 12);
        ops.push({ kind: 'add', val: a, label: `+${a}` });
      } else { // sub
        const b = seededInt(rng, 1, 12);
        ops.push({ kind: 'add', val: -b, label: `${-b}` });
        hasNegative = true;
      }
    }
    if (!hasNegative) {
      const b = seededInt(rng, 1, 12);
      const idx = seededInt(rng, 0, ops.length - 1);
      ops[idx] = { kind: 'add', val: -b, label: `${-b}` };
    }
    const unusualIdx = ops.findIndex(o => o.kind === 'add' && o.val < 0);
    return { ops, unusualIdx };
  }

  // Apply operation
  function applyOp(x, op) {
    if (op.kind === 'add') return x + op.val;
    if (op.kind === 'mul') return x * op.val;
    return x;
  }

  // BFS with bounds and depth cap
  function bfs(start, target, ops, maxSteps, boundAbs) {
    const q = [start];
    const seen = new Map();
    seen.set(start, 0);
    const parent = new Map();
    let head = 0;
    while (head < q.length) {
      const cur = q[head++];
      const d = seen.get(cur);
      if (cur === target) break;
      if (d === maxSteps) continue;
      for (let i = 0; i < ops.length; i++) {
        const op = ops[i];
        const nxt = applyOp(cur, op);
        if (!Number.isFinite(nxt)) continue;
        if (Math.abs(nxt) > boundAbs) continue;
        if (!Number.isInteger(nxt)) continue;
        if (!seen.has(nxt)) {
          seen.set(nxt, d + 1);
          parent.set(nxt, { prev: cur, opIdx: i });
          q.push(nxt);
        }
      }
    }
    const reachable = seen.has(target);
    let path = [], actions = [], depth = null;
    if (reachable) {
      depth = seen.get(target);
      let k = target;
      while (true) {
        path.push(k);
        const p = parent.get(k);
        if (!p) break;
        actions.push(p.opIdx);
        k = p.prev;
      }
      path.reverse();
      actions.reverse();
    }
    return { reachable, depth, path, actions, seen, parent };
  }

  // Render
  function describeQuestion(start, target, ops, minSteps) {
    return `Start at <span class="pill">${start}</span>. You may apply any of the operations shown below in any order (each step uses exactly one operation).<br>
<strong>Task:</strong> Reach <span class="pill good">${target}</span> using as few steps as possible.<br>
<em>Minimum steps</em> (for teacher/grader): <span class="pill">${minSteps}</span>`;
  }

  // Heuristic: removing the unusual op should make target unreachable (or longer) within maxSteps
  function requiresUnusual(start, target, ops, unusualIdx, maxSteps, boundAbs) {
    if (unusualIdx < 0) return false;
    const base = bfs(start, target, ops, maxSteps, boundAbs);
    if (!base.reachable) return false;
    const pruned = ops.slice(0, unusualIdx).concat(ops.slice(unusualIdx + 1));
    const alt = bfs(start, target, pruned, maxSteps, boundAbs);
    return !alt.reachable || (alt.depth > base.depth);
  }

  // Pick start near target with potential negatives
  function pickStart(seed, target) {
    const rng = mulberry32(seed >>> 0);
    const choices = [-15, -12, -9, -7, -5, -3, 3, 5, 7, 9, 12, 15];
    const offset = choice(rng, choices);
    let start = target + offset;
    if (start === target) {
      start = target - 1;
    }
    return start;
  }

  // One attempt
  function attempt(seed, target, numOps, maxSteps) {
    const { ops, unusualIdx } = makeOps(seed, numOps);
    const start = pickStart(seed ^ 0x9e3779b9, target);
    const rough = Math.max(50, Math.abs(target) * 4 + 40);
    const boundAbs = Math.min(2000, rough);
    const res = bfs(start, target, ops, maxSteps, boundAbs);
    return { ops, unusualIdx, start, boundAbs, ...res };
  }

  // Render result
  async function renderResult(r, seed, target, numOps, maxSteps, ideal) {
    if (!r.reachable) {
      setQuestion(`Start at <span class="pill">${r.start}</span>. Target <span class="pill">${target}</span>. (This instance is unreachable within ${maxSteps} steps.)`);
      setOps([]);
      setUnusualIdx(-1);
      setMeta('');
      setPathStates('');
      setPathStatesWithActions('');
      return;
    }
    setQuestion(describeQuestion(r.start, target, r.ops, r.depth));
    setOps(r.ops);
    setUnusualIdx(r.unusualIdx);
    setMeta(`Seed: <span class="pill">${seed}</span> · Bound |x| ≤ <span class="pill">${r.boundAbs}</span> · Shortest steps: <span class="pill good">${r.depth}</span>`);

    const lines = r.path.map(x => String(x));
    setPathStates(lines.join('\n'));

    const withActions = [];
    withActions.push(String(r.path[0]));
    for (let i = 1; i < r.path.length; i++) {
      const opIdx = r.actions[i - 1];
      const op = r.ops[opIdx];
      withActions.push(`${r.path[i]}    (${op.label})`);
    }
    setPathStatesWithActions(withActions.join('\n'));
  }

  // Run with up to 100 variants
  async function run(seedInput, target, numOps, maxSteps) {
    let actualSeed = seedInput;
    if (seedInput === 0) {
      actualSeed = Math.floor(Math.random() * 2 ** 31);
      setSeed(actualSeed);
    }
    numOps = Math.min(6, Math.max(3, numOps));
    maxSteps = Math.min(20, Math.max(3, maxSteps));
    setStatus(`Seed <span class="pill">${actualSeed}</span> · Target <span class="pill">${target}</span> · Ops <span class="pill">${numOps}</span> · Max steps <span class="pill">${maxSteps}</span> · Trying up to <span class="pill">100</span> variants...`);

    let best = null;
    for (let t = 0; t < 100; t++) {
      const testSeed = (actualSeed + t) >>> 0;
      setStatus(`Testing seed <span class="pill">${testSeed}</span> (attempt ${t + 1}/100)...`);
      const r = attempt(testSeed, target, numOps, maxSteps);
      if (r.reachable && r.depth >= 3 && r.depth <= maxSteps && requiresUnusual(r.start, target, r.ops, r.unusualIdx, maxSteps, r.boundAbs)) {
        setSeed(testSeed);
        await renderResult(r, testSeed, target, numOps, maxSteps, true);
        setStatus(`✅ Using seed <span class="pill">${testSeed}</span> — unusual op is necessary; shortest path length <span class="pill good">${r.depth}</span>.`);
        return;
      }
      const score = (r.reachable ? 1 : 0) + (requiresUnusual(r.start, target, r.ops, r.unusualIdx, maxSteps, r.boundAbs) ? 2 : 0) + (r.reachable ? r.depth / 100 : 0);
      if (!best || score > best.score) {
        best = { seed: testSeed, r, score };
      }
      // Add small delay to allow UI to update
      if (t % 10 === 0) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }
    if (best) {
      setSeed(best.seed);
      await renderResult(best.r, best.seed, target, numOps, maxSteps, false);
      setStatus(`⚠️ No perfect match in 100 tries. Showing best attempt (seed ${best.seed}).`);
    } else {
      setStatus(`❌ Could not generate a puzzle. Try other inputs.`);
    }
  }

  const handleRun = () => {
    run(seed, target, numOps, maxSteps);
  };

  const handleNewSeed = () => {
    setSeed(0);
    run(0, target, numOps, maxSteps);
  };

  const handleCopyPath = async () => {
    try {
      await navigator.clipboard.writeText(pathStates || '');
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };

  const handleCopySteps = async () => {
    try {
      await navigator.clipboard.writeText(pathStatesWithActions || '');
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };

  // Auto-run on first load
  useEffect(() => {
    run(0, 23, 4, 12);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="number-steps-generator">
      <header>
        <h1>🔢 Integer Ops Puzzle Generator — Shortest Path</h1>
      </header>
      <div className="wrap">
        <div className="panel">
          <div className="row">
            <div>
              <label>Seed (0 = random; tries +1 up to +99 if needed)</label>
              <input
                type="number"
                value={seed}
                onChange={(e) => setSeed(Number(e.target.value))}
                onKeyDown={(e) => e.key === 'Enter' && handleRun()}
              />
            </div>
            <div>
              <label>Target number</label>
              <input
                type="number"
                value={target}
                onChange={(e) => setTarget(Number(e.target.value))}
                onKeyDown={(e) => e.key === 'Enter' && handleRun()}
              />
            </div>
            <div>
              <label># of operations (3–6)</label>
              <input
                type="number"
                min="3"
                max="6"
                value={numOps}
                onChange={(e) => setNumOps(Number(e.target.value))}
                onKeyDown={(e) => e.key === 'Enter' && handleRun()}
              />
            </div>
            <div>
              <label>Max steps to solution (3–20)</label>
              <input
                type="number"
                min="3"
                max="20"
                value={maxSteps}
                onChange={(e) => setMaxSteps(Number(e.target.value))}
                onKeyDown={(e) => e.key === 'Enter' && handleRun()}
              />
            </div>
            <div className="row">
              <button onClick={handleRun}>Run</button>
              <button className="ghost" onClick={handleNewSeed}>New seed & Run</button>
            </div>
          </div>
          <div className="mt" dangerouslySetInnerHTML={{ __html: status }}></div>
        </div>

        <div className="panel mt">
          <div className="mono" dangerouslySetInnerHTML={{ __html: question }}></div>
          <div className="ops mt">
            {ops.map((op, idx) => (
              <div
                key={idx}
                className="op"
                style={idx === unusualIdx ? { borderColor: 'rgba(250,204,21,.65)' } : {}}
                title={idx === unusualIdx ? 'Unusual op (preferred to be necessary)' : ''}
              >
                {op.label}
              </div>
            ))}
          </div>
          <div className="mono mt" dangerouslySetInnerHTML={{ __html: meta }}></div>
          <h3 className="mt" style={{ marginBottom: '6px' }}>Shortest path states (start → solution)</h3>
          <pre className="mono">{pathStates}</pre>
          <div className="row">
            <button className="ghost" onClick={handleCopyPath}>Copy states</button>
            <button className="ghost" onClick={handleCopySteps}>Copy states + actions</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NumberStepsGenerator;
